import * as yup from 'yup';

import * as api from '../../../api';

import { isNumberValid } from '../../../utils/phoneNumber';

type BaseEditGuarantorValues = Pick<
  api.Guarantor.PrevisitGuarantor,
  'firstName' | 'lastName' | 'address' | 'relationshipToPatient'
>;

type EditGuarantorValuesWithHomePhone = BaseEditGuarantorValues & {
  homePhone?: string | null;
  mobilePhone?: never;
};

type EditGuarantorValuesWithMobilePhone = BaseEditGuarantorValues & {
  homePhone?: never;
  mobilePhone?: string | null;
};

type EditGuarantorValuesWithBothPhones = BaseEditGuarantorValues & {
  homePhone?: string | null;
  mobilePhone?: string | null;
};

export type EditGuarantorValues =
  | EditGuarantorValuesWithHomePhone
  | EditGuarantorValuesWithMobilePhone
  | EditGuarantorValuesWithBothPhones;

export const EditGuarantorSchema = (
  generateErrorMessage: (errorType: string, intlKey: string) => string,
) => {
  return yup.lazy((values: EditGuarantorValues) => {
    return yup.object().shape(
      {
        firstName: yup
          .string()
          .min(1)
          .required(
            generateErrorMessage(
              'editGuarantorModalRequiredFieldError',
              'firstName',
            ),
          ),
        lastName: yup
          .string()
          .min(1)
          .required(
            generateErrorMessage(
              'editGuarantorModalRequiredFieldError',
              'lastName',
            ),
          ),
        homePhone: yup.string().when('mobilePhone', {
          is: (val?: string | null) => [undefined, null, ''].includes(val),
          then: yup
            .string()
            .test(
              'is-valid',
              generateErrorMessage(
                'editGuarantorModalInvalidFieldError',
                'homePhone',
              ),
              () => isNumberValid(values.homePhone ?? ''),
            )
            .required(
              generateErrorMessage(
                'editGuarantorModalRequiredFieldError',
                'homePhone',
              ),
            )
            .nullable(),
          otherwise: yup
            .string()
            .test(
              'is-valid',
              generateErrorMessage(
                'editGuarantorModalInvalidFieldError',
                'homePhone',
              ),
              () => isNumberValid(values.homePhone ?? ''),
            )
            .notRequired()
            .nullable(),
        }),
        mobilePhone: yup.string().when('homePhone', {
          is: (val?: string | null) => [undefined, null, ''].includes(val),
          then: yup
            .string()
            .test(
              'is-valid',
              generateErrorMessage(
                'editGuarantorModalInvalidFieldError',
                'mobilePhone',
              ),
              () => isNumberValid(values.mobilePhone ?? ''),
            )
            .required(
              generateErrorMessage(
                'editGuarantorModalRequiredFieldError',
                'mobilePhone',
              ),
            )
            .nullable(),
          otherwise: yup
            .string()
            .test(
              'is-valid',
              generateErrorMessage(
                'editGuarantorModalInvalidFieldError',
                'mobilePhone',
              ),
              () => isNumberValid(values.mobilePhone ?? ''),
            )
            .notRequired()
            .nullable(),
        }),
        address: yup
          .object()
          .required(
            generateErrorMessage(
              'editGuarantorModalRequiredFieldError',
              'address',
            ),
          ),
        relationshipToPatient: yup
          .string()
          .oneOf(Object.values(api.Common.Relationship))
          .required(
            generateErrorMessage(
              'editGuarantorModalRequiredFieldError',
              'relationshipToPatient',
            ),
          ),
      },
      [['homePhone', 'mobilePhone']],
    );
  });
};
